// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import DataTableContext from "@steiner/MobiusOverrides/DataTable/DataTableContext";
import { color, xScreen } from "@steiner/XDL/Utilities";
import * as React from "react";
import styled, { css, withTheme } from "styled-components";

export type DataTableRowProps = MobiusStyledComponentProps<
    "tr",
    {
        /** CSS string or styled-components function to be injected into this component. */
        css?: StyledProp<DataTableRowProps>;
        evenRowCss?: StyledProp<DataTableRowProps>;
        /**
         * Indicates how the `css` property is combined with the default `css` property from the theme.
         * If true, the default css is applied first and then the component css is applied after causing
         * a merge, much like normal CSS. If false, only the component css is applied, overriding the default css in the theme.
         */
        mergeCss?: boolean;
    }
>;

const mobileTableStyles = css`
    display: flex;
    flex-direction: column;
    background: ${getColor("common.background")};
    padding: 12px;
    row-gap: 10px;
    &:nth-child(even) {
        background: ${getColor("common.accent")};
        ${({ evenRowCss }: any) => evenRowCss}
    }
`;

/**
 * XNG-Changes
 */
const DataTableRowStyle = styled.tr<
    DataTableRowProps & { isMobile?: boolean; useMobileLayout?: boolean; usePrintLayout?: boolean }
>`
    ${({ isMobile, useMobileLayout, evenRowCss }) => {
        if (isMobile && useMobileLayout) {
            return mobileTableStyles;
        }
        return css`
            background: ${getColor("common.background")};
            &:nth-child(even) {
                background: ${getColor("common.ui2")};
                ${evenRowCss}
            }
        `;
    }}

    ${({ usePrintLayout }) =>
        usePrintLayout &&
        css`
            @media print {
                ${mobileTableStyles}
            }
        `}

    ${injectCss}
`;

const DataTableRow: React.FC<DataTableRowProps> = ({ mergeCss, css: dataTableCss = "", ...otherProps }) => {
    const { applyStyledProp } = applyPropBuilder({ ...otherProps }, { component: "dataTable" });
    const { theme } = otherProps;
    const resolvedMergeCss = mergeCss ?? theme?.dataTable.defaultProps?.mergeCss;

    // XNG-Changes: add MobileDataTableRowStyle
    return (
        <DataTableContext.Consumer>
            {({ isMobile, useMobileLayout, usePrintLayout }) => (
                <DataTableRowStyle
                    css={css`
                        ${applyStyledProp(["cssOverrides", "row"], resolvedMergeCss)}
                        ${dataTableCss}
                    `}
                    isMobile={isMobile}
                    useMobileLayout={useMobileLayout}
                    usePrintLayout={usePrintLayout}
                    as={isMobile && useMobileLayout ? "div" : "tr"}
                    evenRowCss={applyStyledProp(["cssOverrides", "evenRow"], resolvedMergeCss)}
                    {...otherProps}
                />
            )}
        </DataTableContext.Consumer>
    );
};

export default withTheme(DataTableRow);
